import React, { useState, useEffect, useRef } from 'react';
import { 
  XMarkIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  SparklesIcon,
  UserIcon,
  ChartBarIcon,
  MapPinIcon,
  CalendarIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { StartupOpportunityUtils } from '../../../../utils/startupOpportunityModels';
import { useOpportunityAnalytics } from '../../../../hooks/useOpportunityAnalytics';
import useScrollLock from '../../../../hooks/useScrollLock';
import { useConnectionStatus } from '../../../../hooks/useConnectionStatus';
import ConnectionStatusButton from './ConnectionStatusButton';

/**
 * OpportunityDetailsModal component for displaying detailed startup opportunity information
 * Features comprehensive company info, founder details, metrics, and connection interface
 */
const OpportunityDetailsModal = ({ 
  opportunity, 
  isOpen, 
  onClose, 
  onConnect,
  className = '',
  source = 'card' // Source context for analytics
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const { trackDetailsView, trackInteraction, trackConversion } = useOpportunityAnalytics();
  const { connectionStatus } = useConnectionStatus(opportunity?.id);
  const hasTrackedDetailsView = useRef(false);
  const viewStartTime = useRef(null);
  const scrollPositions = useRef([]);
  
  // Use scroll lock hook to prevent background scrolling
  const { isLocked } = useScrollLock({ 
    isActive: isOpen,
    restoreScrollOnUnmount: true 
  });

  // Track details view when modal opens
  useEffect(() => {
    if (isOpen && opportunity?.id && !hasTrackedDetailsView.current) {
      viewStartTime.current = Date.now();
      trackDetailsView(opportunity.id, {
        source,
        companyName: opportunity.companyName,
        fundingStage: opportunity.fundingStage,
        roleArea: opportunity.roleArea
      });
      hasTrackedDetailsView.current = true;
    }
    
    // Reset tracking when modal closes
    if (!isOpen) {
      hasTrackedDetailsView.current = false;
      viewStartTime.current = null;
    }
  }, [isOpen, opportunity?.id, trackDetailsView, source, opportunity?.companyName, opportunity?.fundingStage, opportunity?.roleArea]);

  // Track time spent when modal closes
  useEffect(() => {
    return () => {
      if (viewStartTime.current && opportunity?.id) {
        const timeSpent = Date.now() - viewStartTime.current;
        if (timeSpent > 1000) { // Only track if viewed for more than 1 second
          trackDetailsView(opportunity.id, {
            source: 'modal_close',
            timeSpent: Math.round(timeSpent / 1000) // Convert to seconds
          });
          
          // Track engagement metrics
          trackInteraction(opportunity.id, 'details_engagement', {
            timeSpent: Math.round(timeSpent / 1000),
            scrollDepth: scrollPositions.current.length > 0 ? 
              Math.max(...scrollPositions.current) : 0,
            scrollCount: scrollPositions.current.length,
            companyName: opportunity.companyName,
            fundingStage: opportunity.fundingStage,
            roleArea: opportunity.roleArea
          }).catch(err => console.warn('Failed to track details engagement:', err));
        }
      }
    };
  }, [opportunity?.id, trackDetailsView, trackInteraction, opportunity?.companyName, opportunity?.fundingStage, opportunity?.roleArea]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        handleClose(e);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      
      // Focus management - focus the modal container when opened
      const modalContainer = document.querySelector('.modal-container');
      if (modalContainer) {
        modalContainer.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Handle missing opportunity data
  if (!opportunity) {
    return null;
  }

  // Don't render if modal is not open
  if (!isOpen) {
    return null;
  }

  const {
    id,
    companyName = 'Unknown Company',
    companyDescription = '',
    fundingStage = '',
    fundingAmount = '',
    leadInvestors = [],
    roleArea = '',
    roleDescription = '',
    experienceLevel = '',
    isFlexible = true,
    highlights = [],
    founderInfo = null,
    companyMetrics = null,
    requirements = [],
    benefits = [],
    status = 'active'
  } = opportunity;

  // Format display values
  const formattedFundingStage = StartupOpportunityUtils.formatFundingStage(fundingStage);
  const formattedRoleArea = StartupOpportunityUtils.formatRoleArea(roleArea);
  const formattedExperienceLevel = StartupOpportunityUtils.formatExperienceLevel(experienceLevel);

  // Handle connect button click
  const handleConnect = async (e) => {
    e.stopPropagation();
    if (onConnect && typeof onConnect === 'function' && !isConnecting) {
      setIsConnecting(true);
      
      // Track the connect button click
      trackInteraction(opportunity.id, 'details_connect_click', {
        source,
        timeSpent: viewStartTime.current ? 
          Math.round((Date.now() - viewStartTime.current) / 1000) : null,
        companyName: opportunity.companyName,
        fundingStage: opportunity.fundingStage,
        roleArea: opportunity.roleArea
      }).catch(err => console.warn('Failed to track connect click:', err));
      
      // Track conversion from details to connection
      trackConversion(opportunity.id, 'details_to_connection', {
        source,
        timeToConvert: viewStartTime.current ? 
          Math.round((Date.now() - viewStartTime.current) / 1000) : null,
        companyName: opportunity.companyName
      }).catch(err => console.warn('Failed to track conversion:', err));
      
      try {
        await onConnect(opportunity);
      } finally {
        setIsConnecting(false);
      }
    }
  };
  
  // Track scroll position
  const handleScroll = (e) => {
    if (!e.target) return;
    
    const element = e.target;
    const scrollPosition = element.scrollTop / (element.scrollHeight - element.clientHeight);
    
    // Only track significant scroll positions (0-100%)
    const scrollPercentage = Math.floor(scrollPosition * 100);
    
    // Add to scroll positions if it's a new position
    if (scrollPercentage % 25 === 0 && // Track at 0%, 25%, 50%, 75%, 100%
        !scrollPositions.current.includes(scrollPercentage)) {
      scrollPositions.current.push(scrollPercentage);
      
      // Track deep scroll events (50% or more)
      if (scrollPercentage >= 50) {
        trackInteraction(opportunity.id, 'deep_scroll', {
          scrollPercentage,
          timeElapsed: viewStartTime.current ? 
            Math.round((Date.now() - viewStartTime.current) / 1000) : null,
          companyName: opportunity.companyName
        }).catch(err => console.warn('Failed to track deep scroll:', err));
      }
    }
  };

  // Handle modal close
  const handleClose = (e) => {
    e.stopPropagation();
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose(e);
    }
  };

  // Generate company initials for fallback logo
  const getCompanyInitials = () => {
    return companyName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  // Get funding info display
  const getFundingDisplay = () => {
    const parts = [];
    if (formattedFundingStage) parts.push(formattedFundingStage);
    if (fundingAmount) parts.push(fundingAmount);
    return parts.join(' • ');
  };

  return (
    <div 
      className={`modal-backdrop ${className}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <div 
        className="modal-container"
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="modal-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Company Logo/Initials */}
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 
                             flex items-center justify-center text-white font-bold text-xl shadow-lg">
                {getCompanyInitials()}
              </div>
              
              <div>
                <h2 
                  id="modal-title"
                  className="text-2xl font-bold text-gray-900 dark:text-white"
                >
                  {companyName}
                </h2>
                {getFundingDisplay() && (
                  <p className="text-green-600 dark:text-green-400 font-medium">
                    {getFundingDisplay()}
                  </p>
                )}
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 
                       hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div 
          className="modal-scroll-container"
          onScroll={handleScroll}
          id="modal-description"
        >
          <div className="modal-content space-y-8">
            
            {/* Company Overview */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <BuildingOfficeIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                Company Overview
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {companyDescription}
              </p>
            </section>

            {/* Funding & Growth */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <CurrencyDollarIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                Funding & Growth
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Funding Stage */}
                {fundingStage && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Funding Stage</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{formattedFundingStage}</div>
                  </div>
                )}

                {/* Funding Amount */}
                {fundingAmount && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Funding Amount</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{fundingAmount}</div>
                  </div>
                )}

                {/* Lead Investors */}
                {leadInvestors.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 md:col-span-2">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Lead Investors</div>
                    <div className="flex flex-wrap gap-2">
                      {leadInvestors.map((investor, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 
                                   rounded-full text-sm font-medium"
                        >
                          {investor}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Company Metrics */}
            {companyMetrics && (
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <ChartBarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  Company Metrics
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {companyMetrics.teamSize && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                        {companyMetrics.teamSize}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Team Members</div>
                    </div>
                  )}

                  {companyMetrics.clientCount && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                        {companyMetrics.clientCount}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Clients</div>
                    </div>
                  )}

                  {companyMetrics.growth && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">
                        {companyMetrics.growth}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Growth Rate</div>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Founder Information */}
            {founderInfo && (
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  Meet the Founder
                </h3>
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 
                               rounded-xl p-6 border border-purple-100 dark:border-purple-800">
                  <div className="flex items-start gap-4">
                    {/* Founder Avatar */}
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 
                                   flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {founderInfo.name ? founderInfo.name.charAt(0).toUpperCase() : 'F'}
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                        {founderInfo.name}
                      </h4>
                      <p className="text-purple-600 dark:text-purple-400 font-medium mb-3">
                        {founderInfo.title}
                      </p>
                      {founderInfo.bio && (
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                          {founderInfo.bio}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Role Details */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <UserGroupIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Role Details
              </h3>
              
              <div className="space-y-4">
                {/* Role Area and Experience */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Role Area</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{formattedRoleArea}</div>
                  </div>
                  
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Experience Level</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{formattedExperienceLevel}</div>
                  </div>
                </div>

                {/* Role Description */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Role Description</h4>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                      {roleDescription}
                    </p>
                  </div>
                </div>

                {/* Flexible Role Badge */}
                {isFlexible && (
                  <div className="flex items-center gap-2 p-4 bg-amber-50 dark:bg-amber-900/20 
                                 rounded-lg border border-amber-200 dark:border-amber-700">
                    <SparklesIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    <span className="text-amber-800 dark:text-amber-300 font-medium">
                      Title TBD - Role shaped by conversation with founder
                    </span>
                  </div>
                )}
              </div>
            </section>

            {/* Requirements */}
            {requirements.length > 0 && (
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                  What We're Looking For
                </h3>
                <ul className="space-y-2">
                  {requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{requirement}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Benefits */}
            {benefits.length > 0 && (
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <SparklesIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  What We Offer
                </h3>
                <ul className="space-y-2">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <SparklesIcon className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Key Highlights */}
            {highlights.length > 0 && (
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <SparklesIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  Key Highlights
                </h3>
                <ul className="space-y-2">
                  {highlights.map((highlight, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-purple-500 mt-2 flex-shrink-0">•</span>
                      <span className="text-gray-700 dark:text-gray-300">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Status Warning */}
            {status !== 'active' && (
              <div className="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 
                             rounded-lg border border-yellow-200 dark:border-yellow-700">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                <span className="text-yellow-800 dark:text-yellow-300 font-medium">
                  This opportunity is currently {status === 'paused' ? 'paused' : 'filled'} and not accepting new connections.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer with Connect Button */}
        <div className="modal-footer">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Ready to explore this opportunity?
            </div>
            
            <div className="flex-shrink-0">
              <ConnectionStatusButton
                connectionStatus={connectionStatus}
                onConnect={() => handleConnect({ stopPropagation: () => {} })}
                isConnecting={isConnecting}
                disabled={status !== 'active'}
                size="large"
                className="min-w-[200px]"
              />
            </div>
            
            {status !== 'active' && (
              <div id="connect-disabled-reason" className="sr-only">
                This opportunity is currently {status === 'paused' ? 'paused' : 'filled'} and not accepting new connections.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpportunityDetailsModal;